import React, { useEffect, useMemo, useState } from 'react'
import { ModalDialog, useModalActionID } from '../../component/modal-dialog'
import { useTransferContext } from '../transfer-provider'
import { useTransferData } from '../hooks/transfer-data'
import { TransferProgress } from './transfer-progress'
import {
  TRANSFER_EVENT_FAILED,
  TRANSFER_TYPE_COPY_NODE,
  TRANSFER_TYPE_CREATE_TEMPLATE,
  TRANSFER_TYPE_DOWNLOAD,
  TRANSFER_TYPE_UPLOAD
} from '../transfer-types/upload-base'
import { useHistory } from '../../../../node_modules/react-router-dom/cjs/react-router-dom.min'
import { countActiveTransfers, isTransferFinished } from '../transfer-status'
import { IconCopy, IconDownload, IconLink, IconRemove, IconTemplate, IconTransfer, IconTransfers, IconUpload } from '../../icon/icon'

// Follows every transfer's status (each item dispatches on change) and
// counts the running ones.
const useTransferList = () => {
  const transferContext = useTransferContext()

  const list = useMemo(() => Object.values(transferContext.items), [transferContext.items])

  const [activeCount, updateActiveCount] = useState(() => countActiveTransfers(list))

  useEffect(
    () => {
      const recount = () => updateActiveCount(countActiveTransfers(list))
      recount()
      list.forEach(item => item.addEventListener(recount))
      return () => list.forEach(item => item.removeEventListener(recount))
    },
    [list]
  )

  const hasFinished = list.some(isTransferFinished)

  const handleRemoveTransfer = id => transferContext.handleRemoveTransfer(id)
  const handleRemoveCompletedTransfer = () => transferContext.handleRemoveCompletedTransfer()
  const handleCancelTransfer = id => transferContext.handleCancelTransfer(id)

  return { list, activeCount, hasFinished, handleRemoveTransfer, handleRemoveCompletedTransfer, handleCancelTransfer }
}

// The header's transfer tray: this tab's uploads and course, template and
// library copies. Its own icon and name ("Transfers"), never the bell: the
// bell beside it is the notification inbox (owner report 2026-10-19: two
// identical bells on a phone). The button shows ONLY while a transfer is
// running (queued or in progress, transfer-status.js), never for finished
// ones (owner 2026-10-19): how one ended is told by a toast
// (transfer-outcome-toasts.js) and on its own page. Its name shows from
// 992 px; below that it is the tooltip and accessible name.
export const transfersLabel = count => (count > 1 ? `Transfers, ${count} in progress` : count === 1 ? 'Transfers, 1 in progress' : 'Transfers')

export const TransferHeaderList = () => {
  const [target, open, close] = useModalActionID()

  const { list, activeCount, hasFinished, handleRemoveTransfer, handleRemoveCompletedTransfer, handleCancelTransfer } = useTransferList()

  const handleOpenModal = () => open()

  const handleClear = () => {
    const isEmpty = handleRemoveCompletedTransfer()

    if (isEmpty) {
      close()
    }
  }

  const handleRemove = id => {
    handleCancelTransfer(id)
    const isEmpty = handleRemoveTransfer(id)

    if (isEmpty) {
      close()
    }
  }

  const label = transfersLabel(activeCount)

  return (
    <React.Fragment>
      <ModalDialog target={target} Icon={IconTransfers} header="Transfers">
        {hasFinished &&
          <div className="text-end">
            <button type="button" className="btn btn-sm border" onClick={handleClear}>
              Clear finished <IconRemove />
            </button>
          </div>}
        <div>
          {list.map((transfer, i) => <TransferListItem key={i} transfer={transfer} onRemove={handleRemove} onClick={close} />)}
        </div>
      </ModalDialog>
      {activeCount > 0 &&
        <button type="button" className="btn btn-light border mx-1" aria-label={label} title={label} onClick={handleOpenModal} data-testid="header-transfers">
          <IconTransfers />
          <span className="d-none d-lg-inline">Transfers</span>
        </button>}
    </React.Fragment>
  )
}

const TransferListItem = ({ transfer, onRemove, onClick }) => {
  const transferData = useTransferData(transfer)

  const history = useHistory()

  const handleRemove = () => onRemove && onRemove(transfer.id)

  const handleGoto = () => {
    history.push(transferData.gotoPath)
    onClick && onClick()
  }

  return (
    <div className="p-0 border-bottom">
      <div className="d-flex p-1">
        <div className="flex-fill text-truncate">
          <span>
            <TransferType transferData={transferData} />
          </span>
          <span>
            {transferData.name}
          </span>
          {transferData.status === TRANSFER_EVENT_FAILED &&
            <span className="ms-2 text-danger small">Failed</span>}
        </div>
        <div className="edxr-pointer">
          {transferData.gotoPath &&
            <span className="px-1 edxr-pointer" onClick={handleGoto}>
              <IconLink />
            </span>}
          {(isTransferFinished(transferData) || transferData.type === TRANSFER_TYPE_UPLOAD) &&
            <span className="px-1 edxr-pointer" onClick={handleRemove}>
              <IconRemove />
            </span>}
        </div>
      </div>
      <div className="p-1">
        <TransferProgress transferData={transferData} />
      </div>
    </div>
  )
}

const TransferType = ({ transferData }) => {
  const Icon = useMemo(
    () => {
      switch (transferData.type) {
        case TRANSFER_TYPE_DOWNLOAD:
          return IconDownload
        case TRANSFER_TYPE_UPLOAD:
          return IconUpload
        case TRANSFER_TYPE_COPY_NODE:
          return IconCopy
        case TRANSFER_TYPE_CREATE_TEMPLATE:
          return IconTemplate
        default:
          return IconTransfer
      }
    },
    [transferData]
  )

  return (
    <React.Fragment>
      <Icon />
    </React.Fragment>
  )
}
